// Import model
import { Project } from '../models/Project';

// Import base class serializer
import { EntityOwnedModelSerializer } from './EntityOwnedModelSerializer';

// Import dependency types (models and enums used in properties)
import { Customer } from '../models/Customer';
import { SfWorkOrder } from '../models/SfWorkOrder';

// Import dependency serializers (for complex types)
import { CustomerSerializer } from './CustomerSerializer';
import { SfWorkOrderSerializer } from './SfWorkOrderSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for Project - handles JSON serialization and deserialization with type validation
 */
export class ProjectSerializer {
  /**
   * Deserializes a JSON string into a Project instance
   */
  static deserialize(json: string): Project {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a Project instance
   */
  static fromObject(obj: any): Project {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to Project');
    }


    const instance: Project = {
      name: obj['name'] as string | undefined,
      description: obj['description'] as string | undefined,
      entityId: obj['entityId'] as string | undefined,
      entityIdName: obj['entityId|Name'] as string | undefined,
      id: obj['_id'] as string | undefined,
      createdDate: obj['createdDate'] as string | undefined,
      externalId: obj['externalId'] as string | undefined,
      properties: obj['properties'] ? SfWorkOrderSerializer.fromObject(obj['properties']) : undefined as any,
      flowId: obj['flowId'] as string | undefined,
      flowIdName: obj['flowId|Name'] as string | undefined,
      objectStatusId: obj['objectStatusId'] as string | undefined,
      objectStatusIdName: obj['objectStatusId|Name'] as string | undefined,
      customerExternalId: obj['customerExternalId'] as string | undefined,
      customerExternalIdName: obj['customerExternalId|Name'] as string | undefined,
      tags: obj['tags'] ? (obj['tags'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      roomNames: obj['roomNames'] ? (obj['roomNames'] as any[]).map((item: any) =>
        item as string
      ) : [] as any,
      customer: obj['customer'] ? CustomerSerializer.fromObject(obj['customer']) : undefined as any,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of Project instances
   */
  static deserializeArray(json: string): Project[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a Project instance to a JSON string
   */
  static serialize(instance: Project): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a Project instance to a plain object
   */
  static toObject(instance: Project): any {
    return {
      'name': instance.name,
      'description': instance.description,
      'entityId': instance.entityId,
      'entityId|Name': instance.entityIdName,
      '_id': instance.id,
      'createdDate': instance.createdDate,
      'externalId': instance.externalId,
      'properties': instance.properties ? SfWorkOrderSerializer.toObject(instance.properties) : undefined,
      'flowId': instance.flowId,
      'flowId|Name': instance.flowIdName,
      'objectStatusId': instance.objectStatusId,
      'objectStatusId|Name': instance.objectStatusIdName,
      'customerExternalId': instance.customerExternalId,
      'customerExternalId|Name': instance.customerExternalIdName,
      'tags': instance.tags?.map((item: any) =>
        item
      ),
      'roomNames': instance.roomNames?.map((item: any) =>
        item
      ),
      'customer': instance.customer ? CustomerSerializer.toObject(instance.customer) : undefined,
    };
  }

  /**
   * Serializes an array of Project instances to a JSON string
   */
  static serializeArray(instances: Project[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
